import { useEffect, useRef } from 'react';
import {
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetUserQuery,
  useLazyGetUserFavoritesListQuery,
} from '~/redux/api/userApiSlice';
import { getTokenDetails } from '~/redux/utils';
import { readUserFavorite, setUserFavoriteIds } from '~/utils/favorites';

const FavoritesSync = () => {
  const { access_token } = getTokenDetails();
  const isAuthenticated = !!access_token;
  const { data: userData, isLoading: isUserLoading } = useGetUserQuery(
    undefined,
    { skip: !isAuthenticated },
  );
  const [fetchUserFavorites] = useLazyGetUserFavoritesListQuery();
  const [addFavorites] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !userData?.user?.id) {
      hasSynced.current = false;
      return;
    }
    if (isUserLoading || hasSynced.current) return;
    hasSynced.current = true;

    const sync = async () => {
      let serverIds: number[] = [];
      try {
        const data = await fetchUserFavorites().unwrap();
        serverIds = (data ?? [])
          .map((item) => item?.listing?.id)
          .filter((id): id is number => typeof id === 'number');
      } catch (error) {
        console.error('Failed to fetch user favorites:', error);
        return;
      }

      const localFavorite = readUserFavorite();

      if (!localFavorite) {
        setUserFavoriteIds(serverIds);
        return;
      }

      const localIds = localFavorite.payload.listing;
      const serverIdSet = new Set(serverIds);
      const localIdSet = new Set(localIds);

      const toAdd = localIds.filter((id) => !serverIdSet.has(id));
      const toDelete = serverIds.filter((id) => !localIdSet.has(id));

      const tasks: {
        type: 'add' | 'delete';
        ids: number[];
        promise: Promise<unknown>;
      }[] = [];
      if (toAdd.length > 0) {
        tasks.push({
          type: 'add',
          ids: toAdd,
          promise: addFavorites(toAdd).unwrap(),
        });
      }
      toDelete.forEach((id) =>
        tasks.push({
          type: 'delete',
          ids: [id],
          promise: deleteFavorite(id).unwrap(),
        }),
      );

      const addedIds = new Set<number>();
      const deletedIds = new Set<number>();

      if (tasks.length > 0) {
        const results = await Promise.allSettled(tasks.map((t) => t.promise));
        results.forEach((result, index) => {
          const task = tasks[index];
          if (result.status === 'fulfilled') {
            const target = task.type === 'add' ? addedIds : deletedIds;
            task.ids.forEach((id) => target.add(id));
          } else {
            console.error('Failed to sync favorite:', result.reason);
          }
        });
      }

      const finalIds = Array.from(
        new Set([
          ...serverIds.filter((id) => !deletedIds.has(id)),
          ...addedIds,
        ]),
      );
      setUserFavoriteIds(finalIds);
    };

    sync();
  }, [
    isAuthenticated,
    isUserLoading,
    userData?.user?.id,
    fetchUserFavorites,
    addFavorites,
    deleteFavorite,
  ]);

  return null;
};

export default FavoritesSync;
